import React, { useEffect, useState } from 'react';
import {
  getBillboardControl,
  updateBillboardControl,
  BillboardControlState,
} from '../../api/billboard';

interface BillboardControlPanelProps {
  simulatedSpeedMph?: number;
  onSimulatedSpeedChange?: (value: number) => void;
}

export const BillboardControlPanel: React.FC<BillboardControlPanelProps> = ({
  simulatedSpeedMph,
  onSimulatedSpeedChange,
}) => {
  const [state, setState] = useState<BillboardControlState | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchState = async () => {
      try {
        const data = await getBillboardControl();
        if (!cancelled) {
          setState(data);
          setError(null);
        }
      } catch {
        if (!cancelled) {
          setError('Unable to load billboard control state');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchState();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggleBlank = async () => {
    if (!state) return;
    setSaving(true);
    try {
      const updated = await updateBillboardControl({ forceBlank: !state.forceBlank });
      setState(updated);
      setError(null);
    } catch {
      setError('Failed to update billboard control');
    } finally {
      setSaving(false);
    }
  };

  const handleRotationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!state) return;
    const value = Number(event.target.value);
    if (Number.isNaN(value)) return;
    setState({ ...state, rotationSeconds: value });
  };

  const handleSaveRotation = async () => {
    if (!state) return;
    setSaving(true);
    try {
      const updated = await updateBillboardControl({ rotationSeconds: state.rotationSeconds });
      setState(updated);
      setError(null);
    } catch {
      setError('Failed to update rotation interval');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Billboard Control</h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Manually pause or resume billboard playback and tune rotation timing.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading billboard state...</p>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : state ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2">
            <div>
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Blank all billboards</p>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                When enabled, all connected billboard players show a safety screen.
              </p>
            </div>
            <button
              type="button"
              onClick={handleToggleBlank}
              disabled={saving}
              className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition ${
                state.forceBlank
                  ? 'border-red-500 bg-red-500 text-white hover:bg-red-600'
                  : 'border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600'
              } ${saving ? 'opacity-70' : ''}`}
            >
              {state.forceBlank ? 'Currently Blanked' : 'Currently Playing'}
            </button>
          </div>

          <div className="grid gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2 sm:grid-cols-[1fr_auto] sm:items-end">
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
                Rotation interval (seconds)
              </label>
              <input
                type="number"
                min={3}
                max={300}
                value={state.rotationSeconds}
                onChange={handleRotationChange}
                className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                How long each ad should stay on screen before rotating.
              </p>
            </div>
            <button
              type="button"
              onClick={handleSaveRotation}
              disabled={saving}
              className="mt-2 inline-flex items-center justify-center rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
            >
              Save interval
            </button>
          </div>

          <div className="grid gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2 sm:grid-cols-[1fr_auto] sm:items-end">
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">Simulated speed (mph)</label>
              <input
                type="range"
                min={0}
                max={80}
                step={1}
                value={simulatedSpeedMph ?? 35}
                onChange={(e) => onSimulatedSpeedChange?.(Number(e.target.value) || 0)}
                className="mt-1 w-full accent-emerald-500"
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Test how safety blanking responds as speed crosses the limit.
              </p>
            </div>
            <div className="mt-2 text-right text-xs text-slate-600 dark:text-slate-400">
              <div className="tabular-nums">{simulatedSpeedMph ?? 35} mph</div>
              <div className="mt-1 tabular-nums">Limit: {state.speedLimitMph} mph</div>
            </div>
          </div>

          <div className="grid gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2 sm:grid-cols-[1fr_auto] sm:items-end">
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
                Safety speed limit (mph)
              </label>
              <input
                type="number"
                min={10}
                max={80}
                value={state.speedLimitMph}
                onChange={(e) =>
                  setState({ ...state, speedLimitMph: Number(e.target.value) || 45 })
                }
                className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Above this speed, billboards can auto-blank for safety.
              </p>
            </div>
            <button
              type="button"
              onClick={async () => {
                if (!state) return;
                setSaving(true);
                try {
                  const updated = await updateBillboardControl({
                    speedLimitMph: state.speedLimitMph,
                  });
                  setState(updated);
                  setError(null);
                } catch {
                  setError('Failed to update safety speed limit');
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving}
              className="mt-2 inline-flex items-center justify-center rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
            >
              Save speed limit
            </button>
          </div>

          {saving && <p className="text-xs text-slate-500 dark:text-slate-400">Saving changes...</p>}
        </div>
      ) : (
        <p className="text-sm text-slate-500 dark:text-slate-400">No billboard control state available.</p>
      )}
    </div>
  );
};

export default BillboardControlPanel;
